import React from 'react'
import { observer } from 'mobx-react-lite'
import classNames from 'classnames'
import {
    Text,
    Icon,
    FloatingMenu,
    MenuItem,
    Checkbox
} from '@evertel/web/ui'
import { APIDataDocumentACL } from '@evertel/types'
import { DocumentACLsController } from '@evertel/document'

export interface ACLButtonProps {
    attachId?: string
    acl: APIDataDocumentACL
    onChangeACL: (access: string, acl: APIDataDocumentACL) => void
    documentACLController: DocumentACLsController
    /** Staged room that hasn't been saved yet, so it has no ACL id but can still be removed */
    isNew?: boolean
    /** A save is in flight, so no further staging is accepted */
    busy?: boolean
    disabled?: boolean
}

// the four access levels, in the same order and with the same copy as the mobile
// action sheet (libs/mobile/document/src/components/DocumentShareModal.tsx)
const ACCESS_OPTIONS = [
    {
        action: 'read',
        label: 'View Only',
        description: 'Can view but not edit',
        accessType: DocumentACLsController.accessTypes.READ
    },
    {
        action: 'read_share',
        label: 'View and Share',
        description: 'Can view and share with others',
        accessType: DocumentACLsController.accessTypes.READ_AND_SHARE
    },
    {
        action: 'edit',
        label: 'Edit',
        description: 'Can view and edit',
        accessType: DocumentACLsController.accessTypes.EDIT
    },
    {
        action: 'edit_share',
        label: 'Edit and Share',
        description: 'Can view, edit and share with others',
        accessType: DocumentACLsController.accessTypes.EDIT_AND_SHARE
    }
]

const NO_PERMISSION_HINT = 'You do not have permission to grant this level of access'

export const ACLButton: React.FC<ACLButtonProps> = observer(({
    attachId,
    acl,
    documentACLController,
    onChangeACL,
    isNew,
    busy,
    disabled
}) => {

    const isOwner = acl?.accessType === DocumentACLsController.accessTypes.OWNER
    const label = DocumentACLsController.humanizeAccessType(acl?.accessType)

    if (isOwner) {
        return (
            <Text
                italic
                className="me-4">
                {label}
            </Text>
        )
    }

    return (
        <FloatingMenu
            attachId={attachId}
            label={label}
            disabled={busy}
            renderLabel={() => (
                <span className={classNames('text-nowrap', { 'text-danger-emphasis': !acl?.accessType })}>
                    {label}
                    <Icon type="solid" name="caret-down" className="ms-2" />
                </span>
            )}
            className="btn btn-link py-0 px-1"
        >
            {ACCESS_OPTIONS.map((option) => {
                // a user can never grant access above their own level, even on a row they own
                const isDisabled = disabled || busy || !documentACLController.canGrantAccessType(option.accessType)
                const isSelected = acl?.accessType === option.accessType

                return (
                    <MenuItem
                        key={option.action}
                        label={option.label}
                        disabled={isDisabled}
                        onClick={isDisabled ? undefined : () => onChangeACL(option.action, acl)}
                        {...(isDisabled && { title: NO_PERMISSION_HINT })}
                    >
                        <div className="d-flex flex-column">
                            <div className="d-flex align-items-center">
                                <Checkbox
                                    radio
                                    checked={isSelected}
                                    disabled={isDisabled}
                                    inputProps={{ 'aria-hidden': true, tabIndex: -1 }}
                                />
                                <Text
                                    bold
                                    color={isDisabled ? 'muted' : 'primary'}
                                    className="ms-2">
                                    {option.label}
                                </Text>
                            </div>
                            <small className={classNames('ps-4 mt-1', { 'text-muted': isDisabled })}>
                                {option.description}
                            </small>
                        </div>
                    </MenuItem>
                )
            })}
            {(!disabled && !busy && (isNew || !!acl?.id)) && (
                <MenuItem
                    label="Remove"
                    onClick={() => onChangeACL('delete', acl)}
                    icon={{
                        name: 'xmark',
                        color: 'danger-text-emphasis'
                    }}
                    className="text-danger-emphasis"
                />
            )}
        </FloatingMenu>
    )
})
